import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Tab,
  Tabs,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Avatar,
  LinearProgress,
  Stack,
  Fade,
  Zoom,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Description as DocumentIcon,
  Folder as FolderIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  Assessment as StatsIcon,
  Storage as StorageIcon,
  TrendingUp as TrendingIcon,
  AutoAwesome as SparkleIcon,
  SmartToy as AiIcon,
  Policy as PolicyIcon,
} from '@mui/icons-material';
import PolicyModelTab from './PolicyModelTab';

function RagDashboard({ darkMode }) {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('documents');
  const [documents, setDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState({
    totalDocuments: 12,
    totalCollections: 4,
    totalChunks: 156,
    recentUploads: 3,
  });
  const [answer, setAnswer] = useState('');
  const [contextUsed, setContextUsed] = useState([]);
  const [answerLoading, setAnswerLoading] = useState(false);
  const [llm, setLlm] = useState('openai');
  const [model, setModel] = useState('');
  const [policyModelPreselection, setPolicyModelPreselection] = useState({ document: null, collection: null });

  useEffect(() => {
    loadCollections();
  }, []);

  useEffect(() => {
    if (selectedCollection) {
      loadDocuments(selectedCollection);
    }
  }, [selectedCollection]);

  // Load documents when Manage tab is opened (if not already loading)
  useEffect(() => {
    if (activeTab === 2 && selectedCollection && !loading) { // Tab 2 is Manage tab
      // Only load if documents array is empty (might have been cleared)
      if (documents.length === 0) {
        loadDocuments(selectedCollection);
      }
    }
  }, [activeTab, selectedCollection]);

  const loadCollections = async () => {
    try {
      const response = await fetch('/api/v1/rag/collections');
      if (response.ok) {
        const data = await response.json();
        // Defensive: handle both array and object response
        if (Array.isArray(data.collections)) {
          setCollections(data.collections);
        } else if (data.collections && typeof data.collections === 'object') {
          // If collections is an object, convert to array
          setCollections(Object.values(data.collections));
        } else {
          setCollections([]);
        }
      } else {
        setCollections([]);
      }
    } catch (error) {
      // Fallback: show some demo collections if fetch fails
      setCollections([
        { name: 'documents', count: 12 },
        { name: 'company_docs', count: 8 },
        { name: 'support_faq', count: 15 },
      ]);
    }
  };

  const loadDocuments = async (collectionName) => {
    if (!collectionName) {
      console.warn('loadDocuments called without collectionName');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/rag/collections/${collectionName}/documents`);
      const data = await response.json();
      
      if (response.ok && data.success) {
        console.log(`Loaded ${data.documents?.length || 0} documents from collection '${collectionName}'`, data);
        setDocuments(data.documents || []);
      } else {
        console.error('Failed to load documents:', data.error || 'Unknown error', data);
        setDocuments([]);
        if (data.error) {
          alert(`Failed to load documents: ${data.error}`);
        }
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('collection', selectedCollection);

      const response = await fetch('/api/v1/rag/upload', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        alert(`✅ Successfully uploaded ${uploadFile.name}`);
        setUploadFile(null);
        loadCollections();
        loadDocuments(selectedCollection); // Reload documents to show the new upload
      }
    } catch (error) {
      alert(`❌ Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    try {
      const response = await fetch('/api/v1/rag/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: searchQuery,
          collection: selectedCollection,
          top_k: 5,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = async () => {
    if (!searchQuery.trim()) return;
    setAnswer('');
    setContextUsed([]);
    setAnswerLoading(true);
    try {
      const response = await fetch('/api/v1/rag/answer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: searchQuery,
          collection: selectedCollection,
          top_k: 5,
          llm,
          model: model || undefined,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        // Prefer 'answer' if present, else fallback to 'data'
        setAnswer(data.answer ?? data.data ?? '');
        setContextUsed(data.context_used || []);
      }
    } catch (error) {
      setAnswer(`❌ Failed to get answer: ${error.message}`);
    } finally {
      setAnswerLoading(false);
    }
  };

  const renderOverviewTab = () => (
    <Box sx={{ p: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            mb: 1,
          }}
        >
          Knowledge Hub
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Manage your AI's knowledge base with intelligent document processing
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          {
            title: 'Total Documents', value: stats.totalDocuments, icon: DocumentIcon, color: '#2196F3', trend: '+12%',
          },
          {
            title: 'Collections', value: stats.totalCollections, icon: FolderIcon, color: '#4CAF50', trend: '+3%',
          },
          {
            title: 'Knowledge Chunks', value: stats.totalChunks, icon: StorageIcon, color: '#FF9800', trend: '+45%',
          },
          {
            title: 'Recent Uploads', value: stats.recentUploads, icon: TrendingIcon, color: '#9C27B0', trend: 'Today',
          },
        ].map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Zoom in style={{ transitionDelay: `${index * 100}ms` }}>
              <Card sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(stat.color, 0.1)}, ${alpha(stat.color, 0.05)})`,
                border: `1px solid ${alpha(stat.color, 0.2)}`,
                borderRadius: 3,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 25px ${alpha(stat.color, 0.3)}`,
                },
              }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2,
                  }}
                  >
                    <Avatar sx={{
                      bgcolor: stat.color,
                      width: 56,
                      height: 56,
                      boxShadow: `0 4px 20px ${alpha(stat.color, 0.4)}`,
                    }}
                    >
                      <stat.icon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Chip
                      label={stat.trend}
                      size="small"
                      sx={{
                        bgcolor: alpha(stat.color, 0.1),
                        color: stat.color,
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color, mb: 1 }}>
                    {stat.value.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {stat.title}
                  </Typography>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1), rgba(33, 203, 243, 0.05))',
            border: '1px solid rgba(33, 150, 243, 0.2)',
            height: '100%',
          }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{
                  bgcolor: '#2196F3',
                  mr: 2,
                  boxShadow: '0 4px 20px rgba(33, 150, 243, 0.4)',
                }}
                >
                  <CloudUploadIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Upload Documents
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add new knowledge to your AI
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.6 }}>
                Upload PDFs, Word docs, text files, and more. Our AI will automatically chunk and index your content.
              </Typography>
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={() => setActiveTab(1)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Start Uploading
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(76, 175, 80, 0.05))',
            border: '1px solid rgba(76, 175, 80, 0.2)',
            height: '100%',
          }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{
                  bgcolor: '#4CAF50',
                  mr: 2,
                  boxShadow: '0 4px 20px rgba(76, 175, 80, 0.4)',
                }}
                >
                  <SearchIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Intelligent Search
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Find relevant information instantly
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.6 }}>
                Use semantic search to find the most relevant information from your knowledge base.
              </Typography>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => setActiveTab(3)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: '#4CAF50',
                  '&:hover': { bgcolor: '#45a049' },
                }}
              >
                Search Knowledge
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderUploadTab = () => (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
        📤 Upload Documents
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Transform your documents into searchable knowledge
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{
            borderRadius: 3,
            border: '2px dashed',
            borderColor: uploadFile ? 'primary.main' : 'divider',
            bgcolor: uploadFile ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
            transition: 'all 0.3s ease',
            minHeight: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, 0.02),
            },
          }}
          >
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              {!uploadFile ? (
                <Box onClick={() => document.getElementById('file-upload').click()}>
                  <CloudUploadIcon sx={{
                    fontSize: 64, color: 'primary.main', mb: 2, opacity: 0.7,
                  }}
                  />
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Drop files here or click to browse
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Supports PDF, DOCX, TXT, JSON, CSV files up to 10MB
                  </Typography>
                  <Box sx={{
                    display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap',
                  }}
                  >
                    {['PDF', 'DOCX', 'TXT', 'JSON', 'CSV'].map((type) => (
                      <Chip key={type} label={type} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              ) : (
                <Fade in>
                  <Box>
                    <FileIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      📄
                      {' '}
                      {uploadFile.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {(uploadFile.size / (1024 * 1024)).toFixed(2)}
                      {' '}
                      MB
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                      <Button
                        variant="contained"
                        onClick={handleFileUpload}
                        disabled={uploading}
                        startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                        sx={{ borderRadius: 2 }}
                      >
                        {uploading ? 'Uploading...' : 'Upload File'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setUploadFile(null)}
                        sx={{ borderRadius: 2 }}
                      >
                        Remove
                      </Button>
                    </Stack>
                  </Box>
                </Fade>
              )}

              <input
                id="file-upload"
                type="file"
                style={{ display: 'none' }}
                accept=".txt,.pdf,.docx,.json,.csv"
                onChange={(e) => setUploadFile(e.target.files[0])}
              />

              {uploading && (
                <Box sx={{ mt: 3 }}>
                  <LinearProgress sx={{ borderRadius: 1 }} />
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    Processing and indexing your document...
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, height: 'fit-content' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                ⚙️ Upload Settings
              </Typography>

              <TextField
                fullWidth
                label="Collection Name"
                value={selectedCollection}
                onChange={(e) => setSelectedCollection(e.target.value)}
                sx={{ mb: 3 }}
                helperText="Organize documents by topic or project"
              />

              <Box sx={{
                p: 2,
                bgcolor: alpha(theme.palette.info.main, 0.1),
                borderRadius: 2,
                mb: 2,
              }}
              >
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SparkleIcon sx={{ fontSize: 16, mr: 1, color: 'info.main' }} />
                  Automatic text extraction
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AiIcon sx={{ fontSize: 16, mr: 1, color: 'info.main' }} />
                  Smart chunking with overlap
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                  <StorageIcon sx={{ fontSize: 16, mr: 1, color: 'info.main' }} />
                  Vector embedding generation
                </Typography>
              </Box>

              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="caption">
                  Your documents will be processed and made available for AI agents immediately after upload.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderManageTab = () => (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, color: 'primary.main' }}>
        📁 Manage Collections
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Collections</Typography>
              <List>
                {collections.map((collection) => (
                  <ListItem
                    key={collection.name}
                    button
                    selected={selectedCollection === collection.name}
                    onClick={() => {
                      setSelectedCollection(collection.name);
                      loadDocuments(collection.name);
                    }}
                    sx={{ borderRadius: 2, mb: 1 }}
                  >
                    <FolderIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <ListItemText
                      primary={collection.name}
                      secondary={`${collection.count} documents`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Documents in "
                {selectedCollection}
                "
              </Typography>
              {loading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : documents.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <DocumentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography color="text.secondary">No documents found</Typography>
                </Box>
              ) : (
                <List>
                  {documents.map((doc) => (
                    <ListItem
                      key={doc.id}
                      sx={{
                        borderRadius: 2, mb: 1, border: 1, borderColor: 'divider',
                      }}
                    >
                      <DocumentIcon sx={{ mr: 2, color: 'primary.main' }} />
                      <ListItemText
                        primary={doc.filename}
                        secondary={`${doc.chunks} chunks • ${doc.type}`}
                      />
                      <ListItemSecondaryAction>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Create Policy Model">
                            <IconButton
                              edge="end"
                              color="primary"
                              onClick={() => {
                                setPolicyModelPreselection({
                                  document: doc.filename,
                                  collection: selectedCollection
                                });
                                setActiveTab(4);
                              }}
                            >
                              <PolicyIcon />
                            </IconButton>
                          </Tooltip>
                          <IconButton edge="end" color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderSearchTab = () => (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, color: 'primary.main' }}>
        🔍 Search Knowledge Base
      </Typography>

      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Collection"
                value={selectedCollection}
                onChange={(e) => setSelectedCollection(e.target.value)}
                SelectProps={{ native: true }}
              >
                {collections.map((collection) => (
                  <option key={collection.name} value={collection.name}>
                    {collection.name}
                    {' '}
                    (
                    {collection.count}
                    {' '}
                    docs)
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>LLM Provider</InputLabel>
                <Select
                  value={llm}
                  label="LLM Provider"
                  onChange={(e) => setLlm(e.target.value)}
                  size="medium"
                >
                  <MenuItem value="openai">OpenAI</MenuItem>
                  <MenuItem value="ollama">Ollama</MenuItem>
                  <MenuItem value="llama-api">Llama API</MenuItem>
                  <MenuItem value="deepseek">DeepSeek</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Model (optional)"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                fullWidth
                placeholder="e.g. gpt-3.5-turbo, llama3"
                helperText="Model name for LLM"
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Question / Search Query"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                multiline
                minRows={2}
                maxRows={6}
                sx={{ fontSize: '1.1rem' }}
                placeholder="Ask a question about your documents or enter a search query..."
              />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={!searchQuery.trim() || loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                sx={{ borderRadius: 2, minWidth: 120 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleAnswer}
                disabled={!searchQuery.trim() || answerLoading}
                startIcon={answerLoading ? <CircularProgress size={20} /> : <AiIcon />}
                sx={{ borderRadius: 2, minWidth: 120 }}
              >
                Ask LLM
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {searchResults.length > 0 && (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Search Results (
              {searchResults.length}
              )
            </Typography>
            {searchResults.map((result, index) => (
              <Paper
                key={index}
                sx={{
                  p: 3, mb: 2, borderRadius: 2, border: 1, borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Chip label={`Result ${index + 1}`} color="primary" size="small" />
                  <Chip
                    label={`Score: ${result.score?.toFixed(3) || 'N/A'}`}
                    variant="outlined"
                    size="small"
                  />
                </Box>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                  {result.content}
                </Typography>
                {result.metadata && (
                  <Typography variant="caption" color="text.secondary">
                    Source:
                    {' '}
                    {result.metadata.filename || result.metadata.source}
                  </Typography>
                )}
              </Paper>
            ))}
          </CardContent>
        </Card>
      )}
      {answerLoading && (
        <Box sx={{ textAlign: 'center', my: 3 }}>
          <CircularProgress />
          <Typography variant="caption" sx={{ ml: 2 }}>Generating answer...</Typography>
        </Box>
      )}
      {answer && (
        <Card sx={{ borderRadius: 3, mb: 3, bgcolor: '#f9fbe7' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <AiIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
              LLM Answer
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
              {answer}
            </Typography>
            {Array.isArray(contextUsed) && contextUsed.length > 0 && (
              <Box sx={{
                mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1,
              }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Context Used:</Typography>
                <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
                  {contextUsed.join('\n\n---\n\n')}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );

  return (
    <Box sx={{
      height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default',
    }}
    >
      <Box sx={{
        p: 3,
        borderBottom: 1,
        borderColor: 'divider',
        background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.05), rgba(33, 203, 243, 0.02))',
      }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{
            bgcolor: 'primary.main',
            mr: 2,
            width: 48,
            height: 48,
            boxShadow: '0 4px 20px rgba(33, 150, 243, 0.3)',
          }}
          >
            <StorageIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
              RAG Knowledge Hub
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Intelligent document management for your AI agents
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            px: 2,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              minHeight: 60,
              '&.Mui-selected': { color: 'primary.main' },
            },
          }}
        >
          <Tab icon={<StatsIcon />} label="Overview" iconPosition="start" sx={{ gap: 1 }} />
          <Tab icon={<UploadIcon />} label="Upload" iconPosition="start" sx={{ gap: 1 }} />
          <Tab icon={<FolderIcon />} label="Manage" iconPosition="start" sx={{ gap: 1 }} />
          <Tab icon={<SearchIcon />} label="Search" iconPosition="start" sx={{ gap: 1 }} />
          <Tab icon={<PolicyIcon />} label="Policy Models" iconPosition="start" sx={{ gap: 1 }} />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {activeTab === 0 && renderOverviewTab()}
        {activeTab === 1 && renderUploadTab()}
        {activeTab === 2 && renderManageTab()}
        {activeTab === 3 && renderSearchTab()}
        {activeTab === 4 && (
          <PolicyModelTab
            preselectedDocument={policyModelPreselection.document}
            preselectedCollection={policyModelPreselection.collection}
          />
        )}
      </Box>
    </Box>
  );
}

export default RagDashboard;
